import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { getCountryQuestions } from '../utils/countryQuestions';
import { playCorrectSound, playWrongSound } from '../utils/soundEffects';
import { X, ChevronRight, Globe, MapPin } from 'lucide-react';
import confetti from 'canvas-confetti';

const OPTION_COLORS = [
  'bg-emerald-500 border-emerald-700 hover:bg-emerald-400',
  'bg-blue-500 border-blue-700 hover:bg-blue-400',
  'bg-amber-500 border-amber-700 hover:bg-amber-400',
  'bg-rose-500 border-rose-700 hover:bg-rose-400',
];

const CONTINENT_COLORS = {
  Asia: 'bg-red-100 text-red-700',
  Europe: 'bg-blue-100 text-blue-700',
  'North America': 'bg-indigo-100 text-indigo-700',
  'South America': 'bg-emerald-100 text-emerald-700',
  Africa: 'bg-amber-100 text-amber-700',
  Oceania: 'bg-cyan-100 text-cyan-700',
};

export default function CountryPlayPage() {
  const navigate = useNavigate();
  const [questions, setQuestions] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [score, setScore] = useState(0);
  const [selectedOption, setSelectedOption] = useState(null);
  const [showAnswer, setShowAnswer] = useState(false);
  const scoreRef = useRef(0);

  useEffect(() => { scoreRef.current = score; }, [score]);

  useEffect(() => {
    setQuestions(getCountryQuestions(15));
  }, []); // mount-only: generate once

  const currentQ = questions[currentIndex];

  const handleOptionClick = (idx) => {
    if (showAnswer) return;
    setSelectedOption(idx);
    setShowAnswer(true);

    if (idx === currentQ.correctIndex) {
      setScore((s) => s + 1);
      playCorrectSound();
      confetti({ particleCount: 50, spread: 60, origin: { y: 0.7 }, colors: ['#10b981', '#3b82f6', '#f59e0b', '#ef4444'] });
    } else {
      playWrongSound();
    }
  };

  const handleNext = () => {
    if (currentIndex + 1 >= questions.length) {
      navigate(`/country-quiz/results?score=${scoreRef.current}&total=${questions.length}`);
      return;
    }
    setCurrentIndex((i) => i + 1);
    setSelectedOption(null);
    setShowAnswer(false);
  };

  if (!currentQ) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-b from-emerald-50 to-white">
        <p className="font-heading text-2xl text-gray-400">Loading...</p>
      </div>
    );
  }

  const progress = ((currentIndex + 1) / questions.length) * 100;
  const contClass = CONTINENT_COLORS[currentQ.continent] || 'bg-gray-100 text-gray-700';

  return (
    <div className="min-h-screen bg-gradient-to-b from-emerald-50 to-teal-50 flex flex-col">
      {/* Top bar */}
      <div className="bg-white border-b border-gray-100 px-4 py-3">
        <div className="max-w-2xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Globe className="w-5 h-5 text-emerald-500" />
            <span data-testid="country-score" className="font-heading text-2xl font-black text-gray-900">
              {score}/{questions.length}
            </span>
          </div>
          <span data-testid="country-counter" className="font-body text-sm font-bold text-gray-500 bg-gray-100 px-3 py-1 rounded-full">
            {currentIndex + 1} of {questions.length}
          </span>
          <button data-testid="country-quit" onClick={() => navigate('/country-quiz')} className="p-2 rounded-full hover:bg-gray-100 transition-colors">
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>
      </div>

      {/* Progress bar */}
      <div className="h-2 bg-gray-200">
        <div
          data-testid="country-progress"
          className="h-full bg-gradient-to-r from-emerald-400 to-teal-500 rounded-r-full transition-all duration-500 ease-out"
          style={{ width: `${progress}%` }}
        />
      </div>

      {/* Main content */}
      <div className="flex-1 flex flex-col max-w-2xl mx-auto w-full px-4 py-6">
        {/* Continent badge */}
        <div className="text-center mb-2">
          <span className={`text-xs font-bold uppercase tracking-widest px-3 py-1 rounded-full ${contClass}`}>
            {currentQ.continent}
          </span>
        </div>

        <p className="text-center font-heading text-xl sm:text-2xl font-black text-gray-900 mb-4">
          Guess the Country!
        </p>

        {/* Hint Card */}
        <div className="bg-white rounded-3xl shadow-sm border border-gray-100 p-6 sm:p-8 mb-6 text-center">
          <MapPin className="w-8 h-8 text-emerald-500 mx-auto mb-3" />
          <p
            data-testid="country-hint"
            className="font-body text-lg sm:text-xl text-gray-800 leading-relaxed font-bold"
          >
            {currentQ.hint}
          </p>
        </div>

        {/* Options */}
        <div className="grid grid-cols-2 gap-3 mb-4">
          {currentQ.options.map((opt, i) => {
            const optKey = `${currentQ.id}-opt-${opt}`;
            let extraClass = '';
            if (showAnswer) {
              if (i === currentQ.correctIndex) {
                extraClass = 'ring-4 ring-green-400 scale-[1.02]';
              } else if (i === selectedOption && i !== currentQ.correctIndex) {
                extraClass = 'opacity-60 ring-4 ring-red-400';
              } else {
                extraClass = 'opacity-40';
              }
            }

            return (
              <button
                key={optKey}
                data-testid={`country-option-${i}`}
                onClick={() => handleOptionClick(i)}
                disabled={showAnswer}
                className={`${OPTION_COLORS[i]} text-white font-bold text-sm sm:text-base py-4 px-4 rounded-2xl border-b-[5px] ${!showAnswer ? 'active:border-b-0 active:translate-y-[5px]' : ''} transition-all shadow-md text-center ${extraClass}`}
              >
                {opt}
              </button>
            );
          })}
        </div>

        {/* Feedback + Next */}
        {showAnswer && (
          <div className="text-center animate-pop-in">
            {selectedOption === currentQ.correctIndex ? (
              <p data-testid="country-feedback-correct" className="font-heading text-xl font-black text-green-600 mb-3">
                Correct!
              </p>
            ) : (
              <p data-testid="country-feedback-wrong" className="font-heading text-base font-bold text-red-500 mb-3">
                It&apos;s <span className="text-green-700">{currentQ.answer}</span>!
              </p>
            )}
            <button
              data-testid="country-next"
              onClick={handleNext}
              className="bg-gradient-to-r from-emerald-500 to-teal-600 text-white font-black text-base py-3 px-8 rounded-full border-b-[4px] border-teal-800 active:border-b-0 active:translate-y-[4px] transition-all hover:from-emerald-400 hover:to-teal-500 inline-flex items-center gap-2"
            >
              {currentIndex + 1 >= questions.length ? 'See Results' : 'Next Country'}
              <ChevronRight className="w-5 h-5" />
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
